import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCheckedHistory } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { fmtDay } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard from "../CashbookGuard";
import { fmtDateTime } from "../money";
import type { HistoryItem } from "../types";
import { OwnerTabs } from "./OwnerInbox";
import { ownerRefusal } from "./ownerText";
import "../cashbook.scss";
import "./owner.scss";

const HistoryScreen = () => {
  const { t } = useKT();
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (before: string | null) => {
      setBusy(true);
      setError(null);
      try {
        const res = await getCheckedHistory(before, 20);
        if (res?.success && res.data) {
          setItems((old) => (before && old ? [...old, ...res.data.items] : res.data.items));
          setNext(res.data.nextBefore);
        } else setError(ownerRefusal(res, t));
      } catch {
        setError(t("Couldn't load. Check the network."));
      } finally {
        setBusy(false);
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    load(null);
  }, [load]);

  return (
    <div className="cb cbo">
      <PageHeader title={t("Checked history")} back={kp.cashBook()} backLabel={t("Back")} />
      <OwnerTabs on="history" />
      {items === null && !error ? (
        <div role="status" aria-label={t("Loading…")}>
          <div className="cb-skeleton" />
          <div className="cb-skeleton" />
        </div>
      ) : null}
      {items && !items.length ? <p className="cb-muted">{t("Nothing checked yet.")}</p> : null}
      {items?.length ? (
        <ul className="cbo-cards">
          {items.map((h, i) => {
            const title = h.kind === "PAY_SHEET" ? [h.sheetNo, h.holder?.name, fmtDay(h.date)].filter(Boolean).join(" · ") : t("Spends page · {{name}} · {{date}}", { name: h.holder?.name || "", date: fmtDay(h.date) });
            const sub = [
              h.kind === "PAY_SHEET" ? t("{{n}} rows", { n: h.rows }) : t("{{n}} lines", { n: h.rows }),
              h.corrected ? t("{{n}} corrected", { n: h.corrected }) : "",
              h.checkedAt ? t("checked {{when}}", { when: fmtDateTime(h.checkedAt) }) : "",
              h.checkedBy?.length ? t("by {{names}}", { names: h.checkedBy.map((p) => p.name).join(", ") }) : "",
            ]
              .filter(Boolean)
              .join(" · ");
            const body = (
              <span className="cbo-card__main">
                <strong>{title}</strong>
                <span className="cbo-card__sub">{sub}</span>
              </span>
            );
            return (
              <li key={`${h.printId || h.kind}-${h.date}-${i}`}>
                {h.kind === "PAY_SHEET" && h.printId ? (
                  <Link to={kp.cashCheck(h.printId)} className="cbo-card">
                    {body}
                    <span className="kh-btn">{t("Open")}</span>
                  </Link>
                ) : (
                  <div className="cbo-card">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {error ? (
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={() => load(items?.length ? next : null)}>
            {t("Retry")}
          </button>
        </div>
      ) : null}
      {next ? (
        <button type="button" className="cb-btn is-small" disabled={busy} onClick={() => load(next)}>
          {busy ? t("Loading…") : t("Load older")}
        </button>
      ) : null}
    </div>
  );
};

/** /karkhana/money/cash-book/history — checked sheets and spends pages, newest first, 20 at a time. */
const CheckedHistory = () => (
  <CashbookGuard staffOnly>
    <HistoryScreen />
  </CashbookGuard>
);

export default CheckedHistory;
